import { toLabelValue } from '@/common/convertToDropdownOptions';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { useEffect } from 'react';
import { useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import GroupCLubsByLocationFields from './GroupClubsByLocationFields';
import useAssignSubGroupClub from './hooks/useAssignSubGroupClub';

const manageClubsIntoSubGroupFormchema = z.object({
  groupName: z.string().min(1, { message: 'Required' }),
  groupNameKatakana: z.string().min(1, { message: 'Required' }),
  subGroupId: z.string().min(1, { message: 'Required' }),
  locations: z.array(
    z.object({
      locationId: z.string().min(1, 'Required'),
      totalClub: z.string(),
      clubIds: z.array(z.string()).min(1, 'Required'),
    }),
  ),
  grandTotalCounts: z.coerce.number().min(0, { message: 'Grand total must be at least 0' }),
});

export const ClubsIntoSubGroupForm = ({
  defaultGroupDetailValues,
  groupId,
  commonId,
}: {
  defaultGroupDetailValues?: {
    groupName: string;
    groupNameKatakana: string;
  };
  groupId?: string;
  commonId?: string;
}) => {
  const form = useForm({
    schema: manageClubsIntoSubGroupFormchema,
    mode: 'onBlur',
    defaultValues: {
      groupName: defaultGroupDetailValues?.groupName || '',
      groupNameKatakana: defaultGroupDetailValues?.groupNameKatakana || '',
      subGroupId: '',
      locations: [{ locationId: '', totalClub: '', clubIds: [] }],
      grandTotalCounts: 0,
    },
  });

  const { register, control, watch, setValue } = form;
  const { mutateAsync, isPending } = useAssignSubGroupClub(groupId);

  const locationsArray = watch('locations');
  const { data: subGroupOptions = [] } = useDropdown('SubGroupList', commonId);

  const { data: locations = [] } = useDropdown('location');
  const locationOptions = toLabelValue(locations, { labelKey: 'label', valueKey: 'value' });
  const clubLocations = locationsArray.map((location) => location.locationId);
  const validLocationsCount = clubLocations.length < locations.length;

  const total = locationsArray.reduce((acc, curr) => acc + (curr.clubIds?.length || 0), 0);

  useEffect(() => {
    setValue('grandTotalCounts', total);
  }, [total, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locations',
  });
  const { t } = useDynamicLocale();
  return (
    <Form
      form={form}
      className="overflow-y-auto max-h-[553px] scrollbar-none "
      onSubmit={async (data) => {
        if (!groupId) return;
        await mutateAsync({
          groupId,
          subGroupId: data.subGroupId,
          locations: data.locations,
        });
      }}
    >
      <fieldset disabled={subGroupOptions.length === 0 || isPending}>
        <div className="flex flex-col gap-5 p-1 overflow-scroll">
          <div className="grid grid-cols-2 gap-5">
            <Input
              label="configurationManagement.groupsManagement.manageSubGroups.groupName"
              {...register('groupName')}
              disabled
            />
            <Input
              label="configurationManagement.groupsManagement.manageSubGroups.groupNameKatakana"
              {...register('groupNameKatakana')}
              disabled
            />
          </div>

          <Select
            options={subGroupOptions}
            label="configurationManagement.groupsManagement.manageSubGroups.subGroupName"
            {...register('subGroupId')}
            placeholder={t('common.form.placeholder.subGroupName')}
          />

          <div>
            <div className="flex items-center gap-2 mb-4.5">
              <p className="font-semibold ">
                <Translation id="configurationManagement.groupsManagement.manageSubGroups.addClubsIntoSubGroup" />
              </p>
              <div className="border flex-1 h-[1px] items-center "></div>
            </div>

            <ul>
              {fields.map((item, index) => {
                const selectedClubs = watch(`locations.${index}.clubIds`) || [];
                return (
                  <li key={item.id}>
                    <GroupCLubsByLocationFields
                      index={index}
                      fieldsLength={fields.length}
                      selectedClubs={selectedClubs}
                      locationOptions={locationOptions}
                      onRemove={remove}
                    />
                  </li>
                );
              })}
            </ul>

            <div className="mt-3">
              {validLocationsCount && (
                <button
                  type="button"
                  onClick={() => append({ locationId: '', clubIds: [], totalClub: '' })}
                  className="border border-dashed border-primary h-6 text-xs px-4 rounded hover:bg-surface-sunken cursor-pointer"
                >
                  <Translation id="configurationManagement.groupsManagement.manageSubGroups.addNew" />
                </button>
              )}
            </div>
            <div className="mt-3 text-control flex justify-between items-center text-foreground font-medium">
              <Translation id="configurationManagement.groupsManagement.manageSubGroups.grandTotalCounts" />
              <div className="w-24">
                <Input label="" className="text-right" {...form.register('grandTotalCounts')} disabled />
              </div>
            </div>
          </div>
        </div>
      </fieldset>
    </Form>
  );
};
